import type { FC, ReactNode } from 'react';
import { isValidElement, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { sanitizeHtml } from '../../../../components/content-asset/sanitize-html';
import { GetIcon } from '../../../../components/icons';
import { SoftLink } from '../../../../components/soft-link/SoftLink';
import { useSupportsHover } from '../../../../hooks/useSupportsHover';
import { Text } from '../../../../ui-components';
import { toRelativeUrl } from '../../../../utils/url-utils';
import { PageUrl } from '../../../utils/page-url';
import { cleanPathname } from '../../utils/search-url-params-utils';
import type { Block } from '../../../../components/sfcc-content-rendering/Blocks';

interface CategoryLinksProps {
    categorySlots?: Record<string, Block[]>;
    slotId: string;
    currentPathname: string;
}

interface Category {
    id: string;
    c_href?: string;
    name?: string | ReactNode;
}

/**
 * Renders a horizontal swiper carousel for recipe category navigation.
 * Displays "All" category plus additional categories from content slots.
 * @param categorySlots - Content slots containing category data
 * @param slotId - ID of the slot to extract categories from
 * @param currentPathname - Current page path for highlighting active category
 */
export const CategoryLinks: FC<CategoryLinksProps> = ({
    categorySlots,
    slotId,
    currentPathname,
}) => {
    const intl = useIntl();
    const blocks = categorySlots?.[slotId] ?? [];
    const supportsHover = useSupportsHover();

    const categoryBlock = blocks.find((block) => block.type === 'category');

    const allCategories = useMemo<Category[]>(() => {
        if (!categoryBlock || categoryBlock.type !== 'category' || !categoryBlock.items?.length) {
            return [];
        }

        return [
            {
                id: 'all',
                c_href: PageUrl.RECIPES as string,
                name: <FormattedMessage id="recipes.all" defaultMessage="All" />,
            },
            ...categoryBlock.items,
        ];
    }, [categoryBlock]);

    if (allCategories.length === 0) {
        return null;
    }

    const baseButtonClasses =
        'absolute top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white shadow-[0_2px_10px_0_rgba(65,64,66,0.14)] items-center justify-center z-[1] border-0 cursor-pointer';
    const containerClasses = `relative w-full ${supportsHover ? 'px-12' : ''}`;
    const displayClass = supportsHover ? 'flex' : 'hidden';

    return (
        <div className={containerClasses}>
            <button
                className={`category-nav-prev ${baseButtonClasses} ${displayClass} left-0 [&.swiper-button-disabled]:!hidden`}
                type="button"
                aria-label={intl.formatMessage({
                    id: 'recipe.recommendations.previous',
                    defaultMessage: 'Previous',
                })}
            >
                <GetIcon name="navigation-slide-arrow-left" size="md" />
            </button>
            <button
                className={`category-nav-next ${baseButtonClasses} ${displayClass} right-0 [&.swiper-button-disabled]:!hidden`}
                type="button"
                aria-label={intl.formatMessage({
                    id: 'recipe.recommendations.next',
                    defaultMessage: 'Next',
                })}
            >
                <GetIcon name="navigation-slide-arrow-right" size="md" />
            </button>

            <Swiper
                data-testid="category-links-swiper"
                className="w-full"
                modules={[Navigation]}
                slidesPerView="auto"
                spaceBetween={8}
                navigation={{
                    prevEl: '.category-nav-prev',
                    nextEl: '.category-nav-next',
                }}
            >
                {allCategories.map((category) => {
                    const normalizedHref = category.c_href ? toRelativeUrl(category.c_href) : '';
                    const isSelected =
                        cleanPathname(currentPathname) === cleanPathname(normalizedHref);
                    const categoryName: string | ReactNode = category.name ?? '';
                    const displayName = isValidElement(categoryName)
                        ? categoryName
                        : sanitizeHtml(String(categoryName ?? ''), { ALLOWED_TAGS: [] });

                    return (
                        <SwiperSlide key={category.id} style={{ width: 'auto' }}>
                            <SoftLink
                                to={normalizedHref}
                                data-testid={`category-link-${category.id}`}
                                className={`inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-[32px] border border-slt-pepper-300 px-4 py-2 ${
                                    isSelected
                                        ? 'bg-slt-pepper-900'
                                        : 'bg-white hover:bg-slt-pepper-50 hover:shadow-[0_2px_10px_0_rgba(65,64,66,0.14)] active:bg-slt-pepper-100'
                                }`}
                            >
                                <Text
                                    color={isSelected ? 'white' : 'slt-pepper.1000'}
                                    size="base"
                                    style="regular"
                                    letterSpacing="0.5px"
                                >
                                    {displayName}
                                </Text>
                            </SoftLink>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </div>
    );
};

CategoryLinks.displayName = 'CategoryLinks';
